<template>
  <div :class="{ 'login-wrapper': $vuetify.breakpoint.smAndUp || showPrelogin }">
    <v-container class="login-container">
      <v-row justify="start">
        <v-col v-if="showPrelogin" :cols="$vuetify.breakpoint.smAndDown ? 12 : 6">
          <div>
            <sc-placeholder name="prelogincontent" :rendering="rendering" />
            <v-btn
              v-if="$vuetify.breakpoint.smAndDown"
              @click="proceed"
              class="login-proceed"
              outlined
              tile
              color="#00629B"
            >
              <sc-text :field="this.fields.ProceedToLoginLabel" />
            </v-btn>
          </div>
        </v-col>
        <v-col class="okta-col">
          <div
            id="okta-signin-container"
            class=""
            v-bind:class="{ hide: $vuetify.breakpoint.smAndDown && showPrelogin }"
          />
          <sc-placeholder 
            name="helpsection"
            v-show="!($vuetify.breakpoint.smAndDown && showPrelogin)"
            class="help-card--okta"
            :rendering="rendering" />
        </v-col>
      </v-row>
    </v-container>
    <v-dialog v-model="dialogLoggedIn" :persistent="true" width="500">
      <v-card tile class="logout-dialog pb-4">
        <v-card-title class="header py-6">
          <sc-text :field="fields.AlreadyLoggedInTitle" />
        </v-card-title>

        <v-card-text class="content mt-4">
          <sc-rich-text :field="fields.AlreadyLoggedInContent" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn class="btn-secondary pa-sm-4 mr-4" tile @click="logout"><sc-text :field="fields.AlreadyLoggedInLogoutLabel" /></v-btn>
          <v-btn class="btn-primary pa-sm-4 mr-4" tile @click="gotoHome"><sc-text :field="fields.AlreadyLoggedInStayLabel" /></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { config } from '../../auth/config';
import { Text, RichText, isExperienceEditorActive, Placeholder } from '@sitecore-jss/sitecore-jss-vue';

// !important, we need conditional import with OKTA as Content editing functionality will be using SSR and does not have the window context.
// Since Okta sign in has a internal call to Window. with no check we have to do a conditional import
// import OktaSignIn from '@okta/okta-signin-widget';
let OktaSignIn = null;
if (typeof window != 'undefined') {
  OktaSignIn = require('@okta/okta-signin-widget');
}

export default {
  name: 'Login',
  data: function () {
    return {
      showPrelogin: true,
      dialogLoggedIn: false,
    };
  },
  components: {
    ScText: Text,
    ScRichText:RichText,
    ScPlaceholder: Placeholder,
  },
  computed: {
    isSitecoreExperienceEditorOn() {
      // TODO may need to add other check like the preview mode
      return isExperienceEditorActive();
    },
  },
  props: {
    fields: {
      type: Object,
      default: () => ({
        LoginBoxForgotDetailsText: {
          value: 'Help Title',
        },
        LoginBoxCaptionText: {
          value: 'Help Content',
        },
      }),
    },
    rendering: {
      type: Object,
      default: () => ({}),
    },
  },
  mounted: async function () {
    // Check if user already logged in
    this.dialogLoggedIn = false;
    if (this.$auth) {
      let userAuthenticated = await this.$auth.isAuthenticated();
      if (userAuthenticated) {
        let userDetails = await this.$auth.getUser();
        if (userDetails !== null && typeof userDetails != 'undefined') {
          this.dialogLoggedIn = true;
        }
      }
    }
    this.$nextTick(function () {
      // In experience editor we wont import the login widget as it is a conditional Import
      // If it is not imported then we cannot mount
      if (OktaSignIn == null) {
        return;
      }
      const oktaConfig = config(this.$envConfig);
      this.widget = new OktaSignIn({
        /**
         * Note: when using the Sign-In Widget for an OIDC flow, it still
         * needs to be configured with the base URL for your Okta Org. Here
         * we derive it from the given issuer for convenience.
         */
        baseUrl: oktaConfig.oidc.issuer.split('/oauth2')[0],
        clientId: oktaConfig.oidc.clientId,
        redirectUri: oktaConfig.oidc.redirectUri,
        recoveryToken: this.$route.query.recovery_token || null,
        logo: this.fields.LoginBoxLogo.value.src,
        i18n: {
          en: {
            'primaryauth.title': this.fields.LoginBoxTitle.value,
            'primaryauth.username.placeholder': this.fields.LoginBoxUserID.value,
            'primaryauth.submit': this.fields.LoginBoxButtonText.value,
            'error.username.required': this.fields.LoginBoxUsernameRequired.value,
            'error.password.required': this.fields.LoginBoxPasswordRequired.value,
            'errors.E0000005': this.$t('errors-E0000005'),
            'errors.E0000004': this.$t('errors-E0000004'),
            'errors.E0000068': this.$t('errors-E0000068'),
            'errors.E0000069': this.$t('errors-E0000069'),
            'oform.errorbanner.title': this.$t('errors-mandatoryFieldEmpty'),
            'error.auth.lockedOut': this.$t('errors-accountLocked'),
            'model.validation.field.blank': this.$t('validation-mandatoryFieldEmpty'),
            needhelp: 'Need help logging in?',
            forgotpassword: this.$t('password-reset'),
            'password.reset': this.$t('password-reset'),
            'password.reset.title.generic': this.$t('password-reset-title'),
            'password.confirmPassword.placeholder': this.$t('password-confirm-field'),
            'password.confirmPassword.tooltip': this.$t('password-confirm-field'),
            'password.forgot.email.or.username.placeholder': this.$t('password-reset-fieldLabel'),
            'password.forgot.email.or.username.tooltip': this.$t('password-reset-fieldLabel'),
            'password.forgot.sendText': this.$t('password-reset-smsButtonLabel'),
            'password.forgot.sendEmail': this.$t('password-reset-emailButtonLabel'),
            help: this.fields.HelpLink.value.text,
            goback: 'Back to log in', // Reset password back link
            remember: this.fields.LoginBoxRememberMyID.value,
            signout: 'Back to log in', // 2FA back link
            'factor.sms': this.$t('factor-sms-prefix'),
            'factor.sms.time.warning': `Not received an SMS code?<br /><span style="font-weight:bold">Click Re-send to be sent again</span>`,
            'factor.sms.description': 'Enter a single-use code sent to your mobile phone',
            'mfa.challenge.verify': 'Next',
            'mfa.challenge.enterCode.placeholder': 'Enter SMS code',
            'recovery.sms': ' ',
            'recovery.mobile.hint': this.$t('password-reset-hint'),
            'recoveryChallenge.sms.title': '',
            'enroll.choices.title': this.$t('enroll-choices-title'),
            'enroll.choices.description.generic': this.$t('enroll-choices-description-generic'),
            'password.complexity.requirements': this.$t('password-complexity-requirements'),
            'password.complexity.no_username': this.$t('password-complexity-no_username'),
            'password.complexity.history': this.$t('password-complexity-history'),
            'mfa.sendCode': this.$t('mfa-sendcode'),
            'mfa.sent': this.$t('mfa-sent'),
          },
        },
        authParams: {
          pkce: true,
          issuer: oktaConfig.oidc.issuer,
          display: 'page',
          scopes: oktaConfig.oidc.scopes,
          responseMode: 'query',
        },
        helpLinks: {
          help: this.fields.HelpLink.value.href,
          custom: [
            {
              text: 'Forgot user ID?',
              href: '/forgotuserid',
            },
          ],
        },
        colors: {
          brand: '#00629B',
        },
        features: {
          multiOptionalFactorEnroll: true,
          // smsRecovery: true,
          deviceFingerprinting: true,
        },
        transformUsername: function (username, operation) {
          // ensure a valid email can't be submitted on the forgot password screen.
          if (operation === 'FORGOT_PASSWORD') {
            return username.replace('@', '');
          }
          return username;
        },
      });

      this.widget.renderEl(
        { el: '#okta-signin-container' },
        () => {
          /**
           * In this flow, the success handler will not be called because we redirect
           * to the Okta org for the authentication workflow.
           */
        },
        (err) => {
          throw err;
        }
      );
       
      this.widget.on('afterRender', (context) => {
        if (context.controller == 'mfa-verify') {
          const formHeadEl = document.getElementsByClassName('okta-form-title')[0];
          const parentEl = formHeadEl.parentNode;
          const headerEl = document.createElement('div');
          const formTitle = document.querySelector('.okta-form-title')
          const phoneNumber = document.querySelector('.o-form-explain')
          formTitle.textContent = `${formTitle.textContent} ${phoneNumber.textContent} ${this.$t('factor-sms-suffix')}`
          parentEl.insertBefore(headerEl, formHeadEl);
          const headerTextEl = document.createElement('h3');
          headerTextEl.innerText = 'Complete login: Request and enter SMS code';
          headerTextEl.setAttribute('class', 'auth-code-title');
          headerEl.appendChild(headerTextEl);
        } else if (context.controller == 'enroll-choices') {
          //remove MFA enrolment
          document
            .querySelectorAll(
              '.enroll-choices .o-form-fieldset-container, .enroll-choices .o-form-button-bar'
            )
            .forEach((el) => el.remove());
        }
        if (context.controller === 'recovery-challenge') {
          const parentEl = document.querySelector('.o-form-content');
          const titleEl = document.querySelector('.auth-code-title');
          if (!titleEl) {
            parentEl.insertAdjacentHTML(
              'afterbegin',
              `<h3 class="auth-code-title">Reset password using SMS code</h3>
              ${this.$t('password-reset-challenge')}`
            );
          }
        }
        if (context.controller === 'password-reset-email-sent') {
          const parentEl = document.querySelector('.o-form-content');
          const titleEl = document.querySelector('.auth-code-title');
          if (!titleEl) {
            parentEl.insertAdjacentHTML(
              'afterbegin',
              `<h3 class="auth-code-title custom-content">${this.$t(
                'password-reset-email-sent-title'
              )}</h3>
              <div class="sent-tick"></div>
              ${this.$t('password-reset-email-sent-body')}`
            );
          }
        }
        if (context.controller === 'password-reset') {
          const pwordRequirements = document.querySelector('.o-form-explain');
          pwordRequirements.insertAdjacentHTML(
            'afterend',
            this.$t('password-reset-page-second-para')
          );
          const repeatPasswordField = document.querySelector('input[name=confirmPassword]');
          repeatPasswordField.value = '';
        }
      });
    });
    this.$nextTick(function () {
      let userSessionTimedOut = false;
      if (typeof Storage !== 'undefined') {
        // The timedout flag will be send through OKTA as Parameter in Quesry string - Watch for it
        const stateFromQuery = this.$route.query.state;
        if (stateFromQuery === 'SessionTimedOut') {
          userSessionTimedOut = true;
        }
      }
      if (userSessionTimedOut) {
        setTimeout(() => {
          const parent = document.getElementsByClassName('o-form-error-container')[0];
          parent.setAttribute('class', 'o-form-error-container o-form-has-errors');
          const errorEl = document.createElement('div');
          const infoBoxErrorEl = document.createElement('div');
          infoBoxErrorEl.setAttribute('class', 'okta-form-infobox-error infobox infobox-error');
          infoBoxErrorEl.setAttribute('role', 'alert');
          const iconEl = document.createElement('span');
          iconEl.setAttribute('class', 'icon error-16');
          const inactiveEl = document.createElement('p');
          inactiveEl.innerHTML =
            'Your session has timed out due to inactivity. Please log in again.';
          infoBoxErrorEl.appendChild(iconEl);
          infoBoxErrorEl.appendChild(inactiveEl);
          errorEl.appendChild(infoBoxErrorEl);
          parent.appendChild(errorEl);
        }, 250);
      }
    });
  },
  methods: {
    async proceed() {
      this.showPrelogin = false;
      window.scrollTo(0, 0);
    },
    gotoHome() {
      this.$router.push('/');
    },
    logout() {
      this.$router.push('/logout');
    },
  },
  destroyed() {
    // In experience editor we wont import the login widget as it is a conditional Import
    // If it is not imported then we dont need to remove
    if (OktaSignIn == null) {
      return;
    }

    // Remove the widget from the DOM on path change
    if (typeof this.widget != 'undefined') {
      this.widget.remove();
    }
  },
};
</script>

<style lang="scss" scoped>
.login-container {
  .row {
    margin: 0 !important;
  }
  @media #{map-get($display-breakpoints, 'md-and-up')} {
    max-width: 1185px !important;
  }
  @media #{map-get($display-breakpoints, 'sm-only')} {
    padding: 0;
    .col {
      padding: 0;
    }
  }
  @media #{map-get($display-breakpoints, 'xs-only')} {
    margin: 0;
    padding: 15px;
    .row,
    .col {
      padding: 0;
      margin: 0;
    }
  }
}

.login-proceed {
  @media #{map-get($display-breakpoints, 'sm-only')} {
    margin-right: 96px;
  }
  margin-top: 16px;
  float: right;
}

.logout-dialog {
  border-radius: 0px !important;
  .header {
    background-color: map-deep-get($colors, 'blue-banner', 'base') !important;
    color: map-deep-get($colors, 'white', 'base') !important;
    font-size: 22px !important;
    font-weight: 500 !important;
  }
  .content {
    color: map-deep-get($colors, 'grey-cardcontent', 'base') !important;
    font-size: 16px !important;
  }
}
</style>

<style lang="scss">
.v-dialog {
  border-radius: 0px !important;
}
.login-wrapper {
  background-color: map-deep-get($colors, 'white-off', 'base');
  min-height: 80vh;
}

.auth-org-logo {
  margin: 0 0 0 20px !important;

  @media #{map-get($display-breakpoints, 'sm-only')} {
    margin: 0 0 0 20px !important;
  }

  @media #{map-get($display-breakpoints, 'xs-only')} {
    margin: 0 !important;
  }
}

#okta-signin-container {
  @media #{map-get($display-breakpoints, 'sm-only')} {
    margin: 57px 96px 0;
  }
}

.help-card--okta {
  margin: 0;
  
  @media #{map-get($display-breakpoints, 'sm-only')} {
    margin: 0 96px 57px;
  }

  @media #{map-get($display-breakpoints, 'xs-only')} {
    margin: 0 20px 57px;
  }
}

#okta-sign-in {
  border-radius: 0px !important;
  margin: 0px !important;
  width: 100% !important;
  font-family: Roboto !important;
  .infobox {
    hyphens: none !important;
  }

  .auth-content {
    .auth-content-inner {
      .recovery-challenge {
        .button-primary {
          width: 100% !important;
        }
      }
      .auth-code-title {
        font-size: 16px;
        margin-bottom: 12px;
      }
      .mfa-verify {
        .auth-passcode {
          @media #{map-get($display-breakpoints, 'sm-and-up')} {
            float: right !important;
          }
        }
        .sms-request-button {
          @media #{map-get($display-breakpoints, 'xs-only')} {
            line-height: 3.4;
          }
          @media #{map-get($display-breakpoints, 'sm-and-up')} {
            float: left !important;
            width: 150px !important;
            height: 40px !important;
            margin-top: 30px;
          }
          font-size: 14px !important;
          border-radius: 0 !important;
          text-transform: uppercase;
          z-index: 1;
          color: map-deep-get($colors, 'white', 'base') !important;
          background: map-deep-get($colors, 'blue-title', 'base') !important;
          border: 1px solid map-deep-get($colors, 'blue-title', 'base') !important;
          height: 50px !important;
          width: 100% !important;

          &:hover {
            color: map-deep-get($colors, 'blue-title', 'base') !important;
            background: map-deep-get($colors, 'white', 'base') !important;
            border: 1px solid map-deep-get($colors, 'blue-title', 'base') !important;
          }
        }
        .button-primary {
          font-size: 14px !important;
          border-radius: 0 !important;
          text-transform: uppercase;
          z-index: 1;
          color: map-deep-get($colors, 'blue-title', 'base') !important;
          font-weight: 500;
          background-color: map-deep-get($colors, 'white', 'base') !important;
          border: 2px solid map-deep-get($colors, 'blue-title', 'base') !important;
          height: 50px !important;
          width: 100% !important;
          &:hover {
            color: map-deep-get($colors, 'white', 'base') !important;
            background-color: map-deep-get($colors, 'blue-title', 'base') !important;
            border: 1px solid map-deep-get($colors, 'blue-title', 'base') !important;
          }
        }
        .goto {
          float: left !important;
          color: map-deep-get($colors, 'blue-title', 'base') !important;
        }
        h2 + .o-form-explain {
          visibility: hidden;
        }
      }
      .forgot-password {
        .okta-form-title {
          font-weight: bold !important;
        }
        @media #{map-get($display-breakpoints, 'xs-only')} {
          label[for='account-recovery-username'] {
            font-size: 14px !important;
          }
        }
      }
      .password-reset-email-sent,
      .recovery-challenge {
        .custom-content {
          margin-bottom: 10px;
        }
        p {
          &:not(.custom-content) {
            display: none;
          }
        }
        h2:not(.custom-content) {
          display: none;
        }
        .infobox-error p,
        .o-form-input-error {
          display: block !important;
        }
        .sent-tick {
          background-image: url('../../../data/media/img/success_check.svg');
          height: 3rem;
          background-repeat: no-repeat;
          margin: 10px 0;
          background-size: contain;
        }
        .button-wide.button-primary {
          width: auto;
          float: right;
        }
        @media #{map-get($display-breakpoints, 'xs-only')} {
          .o-form-fieldset-container {
            display: flex;
            flex-direction: column-reverse;

            .sms-request-button {
              margin-top: 0 !important;
            }
          }
        }
      }
      .password-reset {
        .okta-form-title {
          font-weight: bold !important;
        }
        .auth-footer .goto {
          float: left !important;
        }
      }

      .enroll-choices {
        &__button {
          min-width: 120px;
          text-align: center;

          @media #{map-get($display-breakpoints, 'xs-only')} {
            display: block;
            float: none !important;
          }
        }

        .okta-form-title {
          font-weight: bold !important;
        }

        .o-form-explain p {
          margin-bottom: 15px;
        }

        .o-form-explain .button {
          float: right;
        }

        .o-form-fieldset-container,
        .o-form-button-bar {
          display: none;
        }
      }
    }
  }
  .js-help {
    display: none !important;
  }

  .help-links {
    display: block !important;
    position: relative;
    top: -52px;

    @media #{map-get($display-breakpoints, 'xs-only')} {
      top: 0;
    }

    .link {
      color: map-deep-get($colors, 'link', 'base') !important;
      font-size: 16px;

      @media #{map-get($display-breakpoints, 'xs-only')} {
        font-size: 14px;
      }
    }
  }
  &.auth-container .o-form-explain {
    text-align: left !important;
  }
  .o-form {
    .custom-checkbox label {
      font-size: 16px !important;
    }
    .o-form-label,
    input,
    label,
    textarea {
      font-family: Roboto !important;
      font-size: 16px !important;
    }

    .button-primary {
      font-size: 14px !important;
      border-radius: 0 !important;
      text-transform: uppercase;
      z-index: 1;
      color: map-deep-get($colors, 'white', 'base') !important;
      background-color: map-deep-get($colors, 'blue-title', 'base') !important;
      border: 1px solid map-deep-get($colors, 'blue-title', 'base') !important;

      &:hover {
        color: map-deep-get($colors, 'blue-title', 'base') !important;
        background-color: map-deep-get($colors, 'white', 'base') !important;
        border: 1px solid map-deep-get($colors, 'blue-title', 'base') !important;
      }
    }
  }
  .o-form-has-errors.input-fix {
    border-color: map-deep-get($colors, 'danger', 'base') !important;
  }
  .auth-footer {
    font-size: 16px;
    a {
      color: map-deep-get($colors, 'link', 'base') !important;
      &:focus {
        box-shadow: none !important;
      }
    }
  }
}

.auth-org-logo {
  max-height: initial !important;
  max-width: initial !important;
}
.okta-form-label {
  font-family: Roboto !important;
  font-weight: normal !important;
  color: #828282 !important;
}
.auth-header {
  z-index: 1 !important;
  padding-left: 0px !important;
  padding-right: 0px !important;
  @media #{map-get($display-breakpoints, 'xs-only')} {
    padding-top: 0 !important;
  }
}
.o-form-button-bar {
  display: flex;
  justify-content: flex-end;

  @media #{map-get($display-breakpoints, 'xs-only')} {
    padding: 0 !important;
  }
}
.focused-input {
  box-shadow: none !important;
}

.o-form-button-bar .button {
  @media #{map-get($display-breakpoints, 'sm-and-up')} {
    min-width: 124px;
    width: auto !important;
  }
}
.auth-content {
  max-width: none !important;
}
.okta-form-title {
  font-family: Roboto !important;
  font-size: 16px !important;
  font-weight: normal !important;
  color: #002033 !important;
  text-align: left !important;
}
.o-form-content,
.o-form-explain:not(.o-form-input-error) {
  font-family: Roboto !important;
  font-size: 16px !important;
  color: #000000 !important;
}

.password-requirements--list {
  margin: 10px 0 !important;
  li {
    padding-bottom: 2px !important;
  }
}

/* SMS MFA page */
.mfa-verify {
  .okta-form-title,
  .okta-form-subtitle {
    display: inline;
    color: map-deep-get($colors, 'blue-text', 'base') !important;
  }

  .okta-form-subtitle::after {
    display: block;
    content: '';
    margin-bottom: 12px;
  }

  .sms-request-button {
    text-transform: uppercase;
  }

  .goto {
    margin-top: 12px !important;
  }

  .o-form-input-name-rememberDevice {
    display: none;
  }
}
</style>
